// GoogleAdHorizontal.tsx
import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";

declare global {
  interface Window {
    GoogleAdHorizontalFixed?: any[];
  }
}

type GoogleAdHorizontalFixedProps = {
  className?: string;
  adSlot?: string;          // e.g. "2206069273" (Leaderboard unit)
  width?: number;           // default 728
  height?: number;          // default 90
  refreshOnPath?: boolean;  // fresh fill when pathname changes
  showTestAds?: boolean;    // show test creatives in dev
};

const AD_CLIENT = "ca-pub-1267919954757840";

/**
 * Fixed-size horizontal Google AdSense unit (e.g., 728x90 leaderboard).
 * This component will:
 *  - Ensure the AdSense loader script is present (once per app)
 *  - Push a fill on mount (and optionally when the route path changes)
 *  - Avoid duplicate pushes in React StrictMode
 *
 * Note: This is a fixed-size unit. Ensure you render it only where the layout
 * can accommodate the given width/height (default 728x90). For responsive ads,
 * use a responsive variant instead of fixed leaderboard sizing.
 */
export default function GoogleAdHorizontalFixed({
  className = "my-4",
  adSlot = "2206069273",
  width = 728,
  height = 140,
  refreshOnPath = true,
  showTestAds = process.env.NODE_ENV !== "production",
}: GoogleAdHorizontalFixedProps) {
  const { pathname } = useLocation();
  const insRef = useRef<HTMLModElement | null>(null);

  // Change key on <ins> so SPA route changes replace the node
  const insKey = refreshOnPath ? `ins-${pathname}-${adSlot}` : `ins-${adSlot}`;

  // Ensure the AdSense loader script exists (once)
  useEffect(() => {
    const existing = document.querySelector<HTMLScriptElement>(
      'script[src*="pagead2.googlesyndication.com/pagead/js/adsbygoogle.js"]'
    );
    if (!existing) {
      const s = document.createElement("script");
      s.async = true;
      s.src = `https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=${AD_CLIENT}`;
      s.crossOrigin = "anonymous";
      s.setAttribute("data-adsbygoogle-loader", "true");
      document.head.appendChild(s);
    }
  }, []);

  // Push a fill for this <ins> (avoid duplicate pushes)
  useEffect(() => {
    const el = insRef.current;
    if (!el) return;

    // Avoid double-push in StrictMode / re-mounts
    if (el.getAttribute("data-adsbygoogle-initialized") === "true") return;

    const push = () => {
      try {
        (window.adsbygoogle = window.adsbygoogle || []).push({});
        el.setAttribute("data-adsbygoogle-initialized", "true");
      } catch {
        // Duplicate/benign errors can occur during development; ignore.
      }
    };

    const hasLoader = !!document.querySelector(
      'script[src*="pagead2.googlesyndication.com/pagead/js/adsbygoogle.js"]'
    );

    if (hasLoader) {
      const t = setTimeout(push, 0); // next tick after mount
      return () => clearTimeout(t);
    }
  }, [insKey]);

  const testAdProps = showTestAds ? ({ "data-adtest": "on" as const } as const) : {};

  return (
    <div
      className={className}
      style={{
        width,         // keep container the same width as the fixed ad
        margin: "0 auto",
      }}
    >
      <ins
        key={insKey}
        ref={insRef as any}
        className="adsbygoogle"
        // Fixed-size leaderboard (from Google's snippet)
        style={{ display: "inline-block", width, height }}
        data-ad-client={AD_CLIENT}
        data-ad-slot={adSlot}
        {...testAdProps}
      />
    </div>
  );
}

